import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Building2, Plus, Sparkles, Save, Loader2, Trash2, Edit3, X, Upload,
  MapPin, Users, Calendar, Globe, RefreshCw, Search, ChevronDown, ChevronUp,
  Eye, Heart, Check, Mail, ArrowRightLeft, UserCircle, Link2, FileText, ZoomIn,
  Instagram, Linkedin,
} from 'lucide-react';
import TikTokIcon from '../../components/TikTokIcon';
import Cropper, { Area } from 'react-easy-crop';
import { useAuth } from '../../hooks/useAuth';
import CompanyCard from '../../components/CompanyCard';
import {
  CEVEE_TAGS_POOL, SIZE_OPTIONS, INDUSTRY_SUGGESTIONS,
} from '../../constants/companyOptions';

async function getCroppedImg(imageSrc: string, crop: Area, bgColor: string): Promise<File> {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = rej; img.src = imageSrc; });

  const size = 400;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, size, size);

  // Map crop area (in original image pixels) to the output canvas
  const sx = Math.max(0, crop.x);
  const sy = Math.max(0, crop.y);
  const sRight = Math.min(img.naturalWidth, crop.x + crop.width);
  const sBottom = Math.min(img.naturalHeight, crop.y + crop.height);
  const sw = sRight - sx;
  const sh = sBottom - sy;
  if (sw > 0 && sh > 0) {
    const dx = ((sx - crop.x) / crop.width) * size;
    const dy = ((sy - crop.y) / crop.height) * size;
    const dw = (sw / crop.width) * size;
    const dh = (sh / crop.height) * size;
    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
  }

  const blob = await new Promise<Blob>((res) => canvas.toBlob((b) => res(b!), 'image/png', 1));
  return new File([blob], 'logo-cropped.png', { type: 'image/png' });
}

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

interface CompanyMember {
  id: string;
  email: string;
  name: string;
}

interface Company {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  bio: string | null;
  industry: string | null;
  size: string | null;
  location: string | null;
  website: string | null;
  cevee_tags: string[];
  looking_for: string | null;
  founded_year: number | null;
  funding_stage: string | null;
  instagram_url: string | null;
  tiktok_url: string | null;
  linkedin_url: string | null;
  is_active: boolean;
  created_by: string;
  interest_count?: number;
  created_at: number;
  owner_email?: string | null;
  owner_name?: string | null;
  member_count?: number;
  members?: CompanyMember[];
}

interface CompanyForm {
  name: string;
  bio: string;
  industry: string;
  size: string;
  location: string;
  website: string;
  cevee_tags: string[];
  looking_for: string;
  founded_year: string;
  funding_stage: string;
  instagram_url: string;
  tiktok_url: string;
  linkedin_url: string;
}

const emptyForm: CompanyForm = {
  name: '', bio: '', industry: '', size: '', location: '', website: '',
  cevee_tags: [], looking_for: '', founded_year: '', funding_stage: '',
  instagram_url: '', tiktok_url: '', linkedin_url: '',
};

function shuffleAndPick<T>(arr: T[], n: number): T[] {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
}

function normalizeSocialUrl(url: string): string | null {
  const t = url?.trim() || '';
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t.replace(/^\/+/, '')}`;
}

export default function CompanyManager() {
  const { apiCall } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // Create/Edit state
  const [mode, setMode] = useState<'list' | 'create' | 'edit'>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CompanyForm>(emptyForm);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [pendingLogo, setPendingLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Logo crop state
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [cropBg, setCropBg] = useState<'#ffffff' | '#000000'>('#ffffff');

  // AI generation
  const [rawText, setRawText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [genLanguage, setGenLanguage] = useState<'de' | 'en'>('de');

  // Website crawler
  const [crawlUrl, setCrawlUrl] = useState('');
  const [crawling, setCrawling] = useState(false);
  const [crawlInfo, setCrawlInfo] = useState<any>(null);

  // Tags
  const [ceveeTagsPreview, setCeveeTagsPreview] = useState(() => shuffleAndPick(CEVEE_TAGS_POOL, 12));
  const [customVibeInput, setCustomVibeInput] = useState('');

  // Delete
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Transfer owner
  const [transferId, setTransferId] = useState<string | null>(null);
  const [transferEmail, setTransferEmail] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Expanded row (show members)
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  const set = (key: keyof CompanyForm, value: any) => setForm(prev => ({ ...prev, [key]: value }));

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/companies/admin/list-all?limit=200`);
      if (resp.ok) {
        const data = await resp.json();
        setCompanies(data.companies || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Failed to load companies:', e);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  const handleGenerate = async () => {
    if (!rawText.trim() || rawText.trim().length < 20) return;
    setGenerating(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/companies/admin/generate-from-context`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ raw_text: rawText, language: genLanguage }),
      });
      if (resp.ok) {
        const data = await resp.json();
        const c = data.company;
        setForm({
          name: c.name || '',
          bio: c.bio || '',
          industry: c.industry || '',
          size: c.size || '',
          location: c.location || '',
          website: c.website || '',
          cevee_tags: c.cevee_tags || [],
          looking_for: c.looking_for || '',
          founded_year: c.founded_year ? String(c.founded_year) : '',
          funding_stage: c.funding_stage || '',
          instagram_url: c.instagram_url || '',
          tiktok_url: c.tiktok_url || '',
          linkedin_url: c.linkedin_url || '',
        });
        setGenerated(true);
      } else {
        const err = await resp.json().catch(() => ({}));
        alert(err.detail || 'Failed to generate company data');
      }
    } catch (e) {
      alert('Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleCrawl = async () => {
    if (!crawlUrl.trim()) return;
    setCrawling(true);
    setCrawlInfo(null);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/companies/admin/crawl-website`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: crawlUrl.trim() }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setRawText(data.crawled_text || '');
        setCrawlInfo(data.crawl_info);
      } else {
        const err = await resp.json().catch(() => ({}));
        alert(err.detail || 'Crawl failed');
      }
    } catch (e) {
      alert('Crawl failed');
    } finally {
      setCrawling(false);
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.bio || !form.industry || !form.size || !form.location) {
      alert('Please fill all required fields: Name, Bio, Industry, Size, Location');
      return;
    }
    if (form.cevee_tags.length === 0) {
      alert('Please select at least 1 Cevee Tag');
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        name: form.name,
        bio: form.bio,
        industry: form.industry,
        size: form.size,
        location: form.location,
        website: form.website || 'https://',
        cevee_tags: form.cevee_tags,
        looking_for: form.looking_for || null,
        founded_year: form.founded_year ? parseInt(form.founded_year) : new Date().getFullYear(),
        funding_stage: form.funding_stage || null,
        instagram_url: normalizeSocialUrl(form.instagram_url),
        tiktok_url: normalizeSocialUrl(form.tiktok_url),
        linkedin_url: normalizeSocialUrl(form.linkedin_url),
      };

      let url: string;
      let method: string;
      if (mode === 'edit' && editingId) {
        url = `${API_BASE}/api/v1/companies/admin/${editingId}`;
        method = 'PUT';
      } else {
        url = `${API_BASE}/api/v1/companies/admin/create`;
        method = 'POST';
      }

      const resp = await apiCall(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (resp.ok) {
        const data = await resp.json();
        const companyId = data.company?.id || editingId;

        const hasExternalLogo = !pendingLogo && logoPreview && logoPreview.startsWith('http');
        if ((pendingLogo || hasExternalLogo) && companyId) {
          await uploadLogo(companyId);
        }

        setMode('list');
        setForm(emptyForm);
        setEditingId(null);
        setLogoUrl(null);
        setPendingLogo(null);
        setLogoPreview(null);
        setRawText('');
        setGenerated(false);
        await loadCompanies();
      } else {
        const err = await resp.json().catch(() => ({}));
        alert(err.detail || 'Failed to save company');
      }
    } catch (e) {
      alert('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (companyId: string) => {
    setUploading(true);
    try {
      if (pendingLogo) {
        // Local file upload
        const fd = new FormData();
        fd.append('file', pendingLogo);
        await apiCall(`${API_BASE}/api/v1/companies/admin/${companyId}/logo`, {
          method: 'POST',
          body: fd,
        });
      } else if (logoPreview && logoPreview.startsWith('http')) {
        // External URL - let backend download it (avoids CORS)
        await apiCall(`${API_BASE}/api/v1/companies/admin/${companyId}/logo-from-url`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: logoPreview }),
        });
      }
    } catch (e) {
      console.error('Logo upload failed:', e);
    } finally {
      setUploading(false);
    }
  };

  const openCropper = async (src: string) => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedArea(null);

    // External URLs get CORS-blocked in canvas - proxy through backend
    if (src.startsWith('http') && !src.startsWith(API_BASE)) {
      try {
        const resp = await apiCall(`${API_BASE}/api/v1/companies/admin/proxy-image?url=${encodeURIComponent(src)}`);
        if (resp.ok) {
          const blob = await resp.blob();
          setCropSrc(URL.createObjectURL(blob));
          return;
        }
      } catch (e) {
        console.warn('Image proxy failed, trying direct:', e);
      }
    }
    setCropSrc(src);
  };

  const handleCropConfirm = async () => {
    if (!cropSrc || !croppedArea) return;
    try {
      const file = await getCroppedImg(cropSrc, croppedArea, cropBg);
      setPendingLogo(file);
      setLogoPreview(URL.createObjectURL(file));
    } catch (e) {
      console.error('Crop failed:', e);
    }
    setCropSrc(null);
  };

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    openCropper(URL.createObjectURL(file));
  };

  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/companies/admin/${id}`, { method: 'DELETE' });
      if (resp.ok) {
        setDeleteConfirmId(null);
        await loadCompanies();
      }
    } catch (e) {
      alert('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleTransferOwner = async (companyId: string) => {
    if (!transferEmail.trim()) return;
    setTransferring(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/companies/admin/${companyId}/transfer-owner`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_owner_email: transferEmail.trim() }),
      });
      if (resp.ok) {
        setTransferId(null);
        setTransferEmail('');
        await loadCompanies();
      } else {
        const err = await resp.json().catch(() => ({}));
        alert(err.detail || 'Transfer failed - user not found?');
      }
    } catch (e) {
      alert('Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const startEdit = (company: Company) => {
    setForm({
      name: company.name || '',
      bio: company.bio || '',
      industry: company.industry || '',
      size: company.size || '',
      location: company.location || '',
      website: company.website || '',
      cevee_tags: company.cevee_tags || [],
      looking_for: company.looking_for || '',
      founded_year: company.founded_year ? String(company.founded_year) : '',
      funding_stage: company.funding_stage || '',
      instagram_url: company.instagram_url || '',
      tiktok_url: company.tiktok_url || '',
      linkedin_url: company.linkedin_url || '',
    });
    setLogoUrl(company.logo_url);
    setEditingId(company.id);
    setPendingLogo(null);
    setLogoPreview(null);
    setCrawlUrl(company.website || '');
    setCrawlInfo(null);
    setRawText('');
    setGenerated(false);
    setMode('edit');
  };

  const startCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setLogoUrl(null);
    setPendingLogo(null);
    setLogoPreview(null);
    setRawText('');
    setCrawlUrl('');
    setCrawlInfo(null);
    setGenerated(false);
    setMode('create');
  };

  const filteredCompanies = searchQuery.trim()
    ? companies.filter(c => {
        const q = searchQuery.toLowerCase();
        return c.name.toLowerCase().includes(q) ||
          (c.industry || '').toLowerCase().includes(q) ||
          (c.location || '').toLowerCase().includes(q) ||
          (c.owner_email || '').toLowerCase().includes(q) ||
          (c.owner_name || '').toLowerCase().includes(q);
      })
    : companies;

  const ADMIN_CREATOR = '00000000-0000-4000-a000-000000000002';

  // ── LIST VIEW ──────────────────────────────────────────────────
  if (mode === 'list') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Company <span className="bg-gradient-to-r from-pink-500 to-orange-400 bg-clip-text text-transparent">Manager</span>
              </h1>
              <p className="text-sm text-slate-500 mt-1">{total} companies total</p>
            </div>
            <button
              onClick={startCreate}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-500 to-orange-400 text-white rounded-xl font-medium text-sm hover:from-pink-600 hover:to-orange-500 transition-all shadow-md"
            >
              <Plus className="w-4 h-4" /> New Company
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search companies..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:border-pink-400 focus:ring-1 focus:ring-pink-200 outline-none"
            />
          </div>

          {/* Company List */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-pink-500" />
            </div>
          ) : (
            <div className="space-y-2">
              {filteredCompanies.map(c => (
                <div key={c.id} className="bg-white rounded-xl border border-slate-200 hover:border-pink-200 transition-all">
                  <div className="p-4 flex items-center gap-4">
                    {/* Logo */}
                    {c.logo_url ? (
                      <img
                        src={`${API_BASE}/api/v1/companies/logos/${c.logo_url}`}
                        alt=""
                        className="w-12 h-12 rounded-lg object-cover border border-slate-100"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-pink-100 to-pink-200 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-pink-500" />
                      </div>
                    )}

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 truncate">{c.name}</h3>
                        {c.created_by === ADMIN_CREATOR && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 font-medium">Platform</span>
                        )}
                        {!c.is_active && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-500 border border-red-200 font-medium">Inactive</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
                        {c.industry && <span>{c.industry}</span>}
                        {c.location && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{c.location}</span>}
                        {c.size && <span className="flex items-center gap-0.5"><Users className="w-3 h-3" />{c.size}</span>}
                        {(c.interest_count || 0) > 0 && (
                          <span className="flex items-center gap-0.5 text-pink-500"><Heart className="w-3 h-3" />{c.interest_count}</span>
                        )}
                      </div>
                      {/* Owner + Members row */}
                      <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                        <span className="flex items-center gap-1" title={`Owner: ${c.created_by}`}>
                          <UserCircle className="w-3 h-3" />
                          {c.owner_email || (c.created_by === ADMIN_CREATOR ? 'Platform' : c.created_by.slice(0, 8))}
                          {c.owner_name && <span className="text-slate-300">({c.owner_name})</span>}
                        </span>
                        <button
                          onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                          className="flex items-center gap-1 hover:text-pink-500 transition-colors"
                        >
                          <Users className="w-3 h-3" />
                          {c.member_count || 0} member{(c.member_count || 0) !== 1 ? 's' : ''}
                          {expandedId === c.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                      </div>
                      {(c.cevee_tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                        {c.cevee_tags.map(tag => (
                          <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded-full bg-gradient-to-r from-pink-600 to-pink-500 text-white font-medium">{tag}</span>
                        ))}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        onClick={() => { setTransferId(transferId === c.id ? null : c.id); setTransferEmail(''); }}
                        className={`p-2 rounded-lg transition-all ${transferId === c.id ? 'text-blue-500 bg-blue-50' : 'text-slate-400 hover:text-blue-500 hover:bg-blue-50'}`}
                        title="Transfer Owner"
                      >
                        <ArrowRightLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => startEdit(c)}
                        className="p-2 rounded-lg text-slate-400 hover:text-pink-500 hover:bg-pink-50 transition-all"
                        title="Edit"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      {deleteConfirmId === c.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(c.id)}
                            disabled={deleting}
                            className="p-2 rounded-lg text-red-500 bg-red-50 hover:bg-red-100 transition-all"
                          >
                            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(null)}
                            className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 transition-all"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(c.id)}
                          className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Transfer owner inline form */}
                  {transferId === c.id && (
                    <div className="px-4 pb-3 border-t border-slate-100 pt-3">
                      <div className="flex items-center gap-2">
                        <ArrowRightLeft className="w-4 h-4 text-blue-500 shrink-0" />
                        <span className="text-xs font-semibold text-slate-600">Transfer owner to:</span>
                        <input
                          value={transferEmail}
                          onChange={e => setTransferEmail(e.target.value)}
                          placeholder="new-owner@email.com"
                          className="flex-1 px-2.5 py-1.5 text-sm rounded-lg border border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-200 outline-none"
                          onKeyDown={e => { if (e.key === 'Enter') handleTransferOwner(c.id); }}
                        />
                        <button
                          onClick={() => handleTransferOwner(c.id)}
                          disabled={transferring || !transferEmail.trim()}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 transition-all"
                        >
                          {transferring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Transfer'}
                        </button>
                        <button onClick={() => { setTransferId(null); setTransferEmail(''); }}
                          className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 transition-all">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1 ml-6">User must have an account on Cevee. This changes who can edit/delete this company.</p>
                    </div>
                  )}

                  {/* Expanded: member list */}
                  {expandedId === c.id && (
                    <div className="px-4 pb-3 border-t border-slate-100 pt-3">
                      <h4 className="text-xs font-semibold text-slate-500 mb-1.5">Members ({c.member_count || 0})</h4>
                      {(c.members || []).length > 0 ? (
                        <div className="space-y-1">
                          {c.members!.map(m => (
                            <div key={m.id} className="flex items-center gap-2 text-xs text-slate-600">
                              <Mail className="w-3 h-3 text-slate-400" />
                              <span className="font-medium">{m.name}</span>
                              <span className="text-slate-400">{m.email}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400">No members linked to this company</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {filteredCompanies.length === 0 && (
                <p className="text-center text-slate-400 py-12">No companies found</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── CREATE / EDIT VIEW ──────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100">
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <button onClick={() => { setMode('list'); setGenerated(false); }} className="text-sm text-slate-400 hover:text-slate-600 mb-1 flex items-center gap-1">
              &larr; Back to list
            </button>
            <h1 className="text-xl font-bold text-gray-900">
              {mode === 'edit' ? 'Edit' : 'Create'} Company{' '}
              <span className="bg-gradient-to-r from-pink-500 to-orange-400 bg-clip-text text-transparent">
                {mode === 'edit' ? form.name : 'Profile'}
              </span>
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: Form */}
          <div className="lg:col-span-3 space-y-4">
            {/* AI Generation + Website Crawler */}
              <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-pink-500" />
                  <h3 className="text-sm font-semibold text-gray-900">{mode === 'edit' ? 'AI Re-generate / Update' : 'AI Generate Profile'}</h3>
                  <div className="ml-auto flex items-center rounded-lg border border-slate-200 overflow-hidden">
                    <button onClick={() => setGenLanguage('de')}
                      className={`px-2.5 py-1 text-[10px] font-medium transition-all ${genLanguage === 'de' ? 'bg-pink-500 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                      🇩🇪 DE
                    </button>
                    <button onClick={() => setGenLanguage('en')}
                      className={`px-2.5 py-1 text-[10px] font-medium transition-all ${genLanguage === 'en' ? 'bg-pink-500 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                      🇬🇧 EN
                    </button>
                  </div>
                </div>

                {/* Step 1: Crawl from URL (optional) */}
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Link2 className="w-3.5 h-3.5 text-blue-500" />
                    <span className="text-xs font-semibold text-slate-600">Step 1: Fetch from Website</span>
                    <span className="text-[10px] text-slate-400">(optional)</span>
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={crawlUrl}
                      onChange={e => setCrawlUrl(e.target.value)}
                      placeholder="https://startup.com"
                      className="flex-1 px-2.5 py-1.5 text-sm rounded-lg border border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-200 outline-none"
                      onKeyDown={e => { if (e.key === 'Enter') handleCrawl(); }}
                    />
                    <button
                      onClick={handleCrawl}
                      disabled={crawling || !crawlUrl.trim()}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white rounded-lg font-medium text-xs hover:bg-blue-600 disabled:opacity-50 transition-all"
                    >
                      {crawling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />}
                      {crawling ? 'Crawling...' : 'Fetch'}
                    </button>
                  </div>
                </div>

                {/* Crawl info panel */}
                {crawlInfo && (
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-100 space-y-2">
                    <div className="flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5 text-blue-500" />
                      <span className="text-xs font-semibold text-blue-700">Crawl Results</span>
                      <span className="ml-auto text-[10px] text-blue-400">{crawlInfo.text_length.toLocaleString()} chars</span>
                    </div>
                    <div className="text-[10px] text-blue-600 space-y-0.5">
                      <p><span className="font-medium">Pages:</span> {crawlInfo.pages_crawled?.map((p: string) => p.replace(/^https?:\/\/(www\.)?/, '')).join(' · ')}</p>
                      {crawlInfo.logo_candidates?.length > 0 && (
                        <div>
                          <span className="font-medium">Logo candidates:</span>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {crawlInfo.logo_candidates.map((url: string, i: number) => (
                              <img key={i} src={url} alt="" className="w-8 h-8 rounded border border-blue-200 object-contain bg-white cursor-pointer hover:ring-2 hover:ring-blue-400"
                                onClick={() => openCropper(url)}
                                title="Click to crop & use as logo"
                              />
                            ))}
                            {crawlInfo.clearbit_logo && (
                              <img src={crawlInfo.clearbit_logo} alt="" className="w-8 h-8 rounded border border-blue-200 object-contain bg-white cursor-pointer hover:ring-2 hover:ring-blue-400"
                                onClick={() => openCropper(crawlInfo.clearbit_logo)}
                                title="Clearbit logo - click to crop & use"
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 2: Text area (pre-filled by crawl or manual paste) */}
                <div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <FileText className="w-3.5 h-3.5 text-pink-500" />
                    <span className="text-xs font-semibold text-slate-600">Step 2: Review & Add Info</span>
                  </div>
                  <textarea
                    value={rawText}
                    onChange={e => setRawText(e.target.value)}
                    rows={6}
                    placeholder="Crawled text appears here - or paste company info manually (website text, LinkedIn, job postings, press releases, anything)"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:border-pink-400 focus:ring-1 focus:ring-pink-200 outline-none resize-y"
                  />
                </div>

                {/* Step 3: Generate */}
                <button
                  onClick={handleGenerate}
                  disabled={generating || rawText.trim().length < 20}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-500 to-orange-400 text-white rounded-lg font-medium text-sm hover:from-pink-600 hover:to-orange-500 disabled:opacity-50 transition-all"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {generating ? 'Generating...' : mode === 'edit' ? 'Re-generate from Text' : 'Generate Profile from Text'}
                </button>

                {generated && (
                  <p className="text-xs text-green-600 flex items-center gap-1"><Check className="w-3 h-3" /> Fields populated - review and edit below</p>
                )}
              </div>

            {/* Form fields */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
              {/* Logo */}
              <div className="flex items-center gap-3">
                {logoPreview ? (
                  <img src={logoPreview} alt="" className="w-14 h-14 rounded-xl object-cover border border-slate-100" />
                ) : logoUrl ? (
                  <img src={`${API_BASE}/api/v1/companies/logos/${logoUrl}`} alt="" className="w-14 h-14 rounded-xl object-cover border border-slate-100" />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-100 to-pink-200 flex items-center justify-center">
                    <Building2 className="w-7 h-7 text-pink-400" />
                  </div>
                )}
                <div>
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200 transition-colors"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    {logoUrl || logoPreview ? 'Change Logo' : 'Upload Logo'}
                  </button>
                  {pendingLogo && <p className="text-[10px] text-pink-500 mt-0.5">Will upload on save</p>}
                </div>
                <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={handleLogoSelect} className="hidden" />
              </div>

              {/* Name + Industry */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Name *</label>
                  <input value={form.name} onChange={e => set('name', e.target.value)}
                    className="mt-0.5 w-full px-2.5 py-1.5 text-sm rounded-lg border border-slate-300 focus:border-pink-400 focus:ring-1 focus:ring-pink-200 outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Industry *</label>
                  <input value={form.industry} onChange={e => set('industry', e.target.value)} list="industry-list"
                    className="mt-0.5 w-full px-2.5 py-1.5 text-sm rounded-lg border border-slate-300 focus:border-pink-400 focus:ring-1 focus:ring-pink-200 outline-none" />
                  <datalist id="industry-list">
                    {INDUSTRY_SUGGESTIONS.map(i => <option key={i} value={i} />)}
                  </datalist>
                </div>
              </div>

              {/* Location + Size */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><MapPin className="w-3 h-3" /> Location *</label>
                  <input value={form.location} onChange={e => set('location', e.target.value)}
                    className="mt-0.5 w-full px-2.5 py-1.5 text-sm rounded-lg border border-slate-300 focus:border-pink-400 focus:ring-1 focus:ring-pink-200 outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><Users className="w-3 h-3" /> Size *</label>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {SIZE_OPTIONS.map(s => (
                      <button key={s} onClick={() => set('size', form.size === s ? '' : s)}
                        className={`px-2 py-0.5 rounded-full text-xs font-medium border-2 transition-all ${
                          form.size === s ? 'bg-pink-50 border-pink-400 text-pink-600' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                        }`}>{s}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Website + Founded */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><Globe className="w-3 h-3" /> Website</label>
                  <input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://..."
                    className="mt-0.5 w-full px-2.5 py-1.5 text-sm rounded-lg border border-slate-300 focus:border-pink-400 focus:ring-1 focus:ring-pink-200 outline-none" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><Calendar className="w-3 h-3" /> Founded</label>
                  <input value={form.founded_year} onChange={e => set('founded_year', e.target.value)} type="number" placeholder="2020"
                    className="mt-0.5 w-full px-2.5 py-1.5 text-sm rounded-lg border border-slate-300 focus:border-pink-400 focus:ring-1 focus:ring-pink-200 outline-none" />
                </div>
              </div>

              {/* Social Links (optional) */}
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Social Links (optional)</label>
                <div className="mt-0.5 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="relative">
                    <TikTokIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input value={form.tiktok_url} onChange={e => set('tiktok_url', e.target.value)} placeholder="tiktok.com/@..."
                      className="w-full pl-7 pr-2 py-1.5 text-sm rounded-lg border border-slate-300 focus:border-pink-400 focus:ring-1 focus:ring-pink-200 outline-none" />
                  </div>
                  <div className="relative">
                    <Instagram className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input value={form.instagram_url} onChange={e => set('instagram_url', e.target.value)} placeholder="instagram.com/..."
                      className="w-full pl-7 pr-2 py-1.5 text-sm rounded-lg border border-slate-300 focus:border-pink-400 focus:ring-1 focus:ring-pink-200 outline-none" />
                  </div>
                  <div className="relative">
                    <Linkedin className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input value={form.linkedin_url} onChange={e => set('linkedin_url', e.target.value)} placeholder="linkedin.com/company/..."
                      className="w-full pl-7 pr-2 py-1.5 text-sm rounded-lg border border-slate-300 focus:border-pink-400 focus:ring-1 focus:ring-pink-200 outline-none" />
                  </div>
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">About *</label>
                <textarea value={form.bio} onChange={e => set('bio', e.target.value)} rows={3} placeholder="Cevee-style bio..."
                  className="mt-0.5 w-full px-2.5 py-1.5 text-sm rounded-lg border border-slate-300 focus:border-pink-400 focus:ring-1 focus:ring-pink-200 outline-none resize-none" />
              </div>

              {/* Looking for */}
              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Looking for</label>
                <input value={form.looking_for} onChange={e => set('looking_for', e.target.value)} placeholder="Roles, positions..."
                  className="mt-0.5 w-full px-2.5 py-1.5 text-sm rounded-lg border border-slate-300 focus:border-pink-400 focus:ring-1 focus:ring-pink-200 outline-none" />
              </div>

              {/* Cevee Tags */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                    <span className="bg-gradient-to-r from-pink-500 to-pink-600 bg-clip-text text-transparent">✦</span> Cevee Vibes * ({form.cevee_tags.length}/5)
                  </label>
                  <button onClick={() => setCeveeTagsPreview(shuffleAndPick(CEVEE_TAGS_POOL, 12))}
                    className="p-0.5 rounded text-pink-400 hover:text-pink-600 transition-colors"><RefreshCw className="w-3 h-3" /></button>
                </div>
                {/* Selected */}
                {form.cevee_tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-1.5">
                    {form.cevee_tags.map(tag => (
                      <button key={tag} onClick={() => set('cevee_tags', form.cevee_tags.filter(t => t !== tag))}
                        className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-gradient-to-r from-pink-600 to-pink-500 text-white flex items-center gap-0.5">
                        {tag} <X className="w-2.5 h-2.5" />
                      </button>
                    ))}
                  </div>
                )}
                {/* Pool */}
                <div className="flex flex-wrap gap-1">
                  {ceveeTagsPreview.filter(t => !form.cevee_tags.includes(t)).map(tag => (
                    <button key={tag} onClick={() => { if (form.cevee_tags.length < 5) set('cevee_tags', [...form.cevee_tags, tag]); }}
                      disabled={form.cevee_tags.length >= 5}
                      className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-white border border-slate-200 text-slate-500 hover:border-pink-300 hover:text-pink-500 disabled:opacity-30 transition-all">
                      {tag}
                    </button>
                  ))}
                </div>
                {/* Custom */}
                <div className="flex gap-1.5 mt-1.5">
                  <input value={customVibeInput} onChange={e => setCustomVibeInput(e.target.value)} placeholder="Custom tag..."
                    onKeyDown={e => {
                      if (e.key === 'Enter' && customVibeInput.trim() && form.cevee_tags.length < 5) {
                        set('cevee_tags', [...form.cevee_tags, customVibeInput.trim()]);
                        setCustomVibeInput('');
                      }
                    }}
                    className="flex-1 px-2 py-1 text-xs rounded-lg border border-slate-200 focus:border-pink-400 outline-none" />
                  <button onClick={() => {
                    if (customVibeInput.trim() && form.cevee_tags.length < 5) {
                      set('cevee_tags', [...form.cevee_tags, customVibeInput.trim()]);
                      setCustomVibeInput('');
                    }
                  }} className="px-2 py-1 text-xs font-medium rounded-lg bg-pink-50 text-pink-500 border border-pink-200 hover:bg-pink-100 transition-colors">
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {/* Save */}
              <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-pink-500 to-orange-400 text-white rounded-xl font-medium text-sm hover:from-pink-600 hover:to-orange-500 disabled:opacity-50 transition-all shadow-md"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? 'Saving...' : mode === 'edit' ? 'Update Company' : 'Create Company'}
                </button>
                <button onClick={() => { setMode('list'); setGenerated(false); }}
                  className="px-4 py-2.5 text-sm text-slate-500 hover:text-slate-700 transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          </div>

          {/* Right: Preview */}
          <div className="lg:col-span-2">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
              <Eye className="w-3 h-3" /> Card Preview
            </h3>
            <div className="max-w-[340px]">
              <CompanyCard
                company={{
                  name: form.name || 'Company Name',
                  logo_url: logoPreview || logoUrl,
                  bio: form.bio || null,
                  industry: form.industry || null,
                  size: form.size || null,
                  location: form.location || null,
                  website: form.website || null,
                  cevee_tags: form.cevee_tags,
                  looking_for: form.looking_for || null,
                  founded_year: form.founded_year ? parseInt(form.founded_year) : null,
                }}
                showActions={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Logo Crop Modal */}
      {cropSrc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-[90vw] max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <ZoomIn className="w-4 h-4 text-pink-500" />
                <span className="text-sm font-semibold text-gray-900">Crop Logo</span>
              </div>
              <button onClick={() => setCropSrc(null)} className="p-1 hover:bg-slate-100 rounded-lg transition-colors">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
            <div className="relative w-full h-72" style={{ background: cropBg === '#ffffff' ? '#f1f5f9' : '#1e293b' }}>
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                minZoom={0.1}
                maxZoom={4}
                aspect={1}
                cropShape="round"
                showGrid={false}
                restrictPosition={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, area) => setCroppedArea(area)}
                style={{ containerStyle: { background: cropBg === '#ffffff' ? '#f1f5f9' : '#1e293b' } }}
              />
            </div>
            <div className="px-4 py-3 space-y-3">
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-slate-400 font-medium">ZOOM</span>
                <input
                  type="range"
                  min={0.1} max={4} step={0.05}
                  value={zoom}
                  onChange={e => setZoom(Number(e.target.value))}
                  className="flex-1 accent-pink-500 h-1.5"
                />
                <span className="text-[10px] text-slate-500 w-8 text-right">{zoom.toFixed(1)}x</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-slate-400 font-medium">BG</span>
                <div className="flex gap-1.5">
                  <button onClick={() => setCropBg('#ffffff')}
                    className={`w-6 h-6 rounded-full border-2 bg-white ${cropBg === '#ffffff' ? 'border-pink-500 ring-2 ring-pink-200' : 'border-slate-300'}`}
                    title="White background" />
                  <button onClick={() => setCropBg('#000000')}
                    className={`w-6 h-6 rounded-full border-2 bg-black ${cropBg === '#000000' ? 'border-pink-500 ring-2 ring-pink-200' : 'border-slate-300'}`}
                    title="Black background" />
                </div>
                <span className="text-[10px] text-slate-400">Fill when zoomed out</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCropConfirm}
                  className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-gradient-to-r from-pink-500 to-orange-400 text-white rounded-lg font-medium text-sm hover:from-pink-600 hover:to-orange-500 transition-all"
                >
                  <Check className="w-3.5 h-3.5" /> Use This
                </button>
                <button
                  onClick={() => setCropSrc(null)}
                  className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
